import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import swal from 'sweetalert'

import { CatchError } from 'Exceptions/exceptions'
import UserService from 'Main/service/UserService'
import CommonListApiPaginated from 'views/components/Table/CommonListApiPaginated'
import { getRefreshToken } from 'variables/common'
import { AddUserModal } from './Modal/AddUserModal'

function UserManager () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const userService = useMemo(() => new UserService(), [])
  const [reloadKey, setReloadKey] = useState(0)
  const [showAddModal, setShowAddModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const getUsers = useCallback(() => userService.getUsers(), [userService])

  const handleListError = useCallback((error) => {
    CatchError(error, navigate, refreshToken)
  }, [navigate, refreshToken])

  const handleView = useCallback((id) => {
    navigate(`/dashboard/profile/${id}`)
  }, [navigate])

  const handleAddSubmit = async (form) => {
    try {
      setSubmitting(true)
      await userService.createUser({ ...form, type: 0 })
      setShowAddModal(false)
      await swal('Success!', 'User created', 'success')
      setReloadKey((current) => current + 1)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) await swal('Error', 'Failed to create user', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = useCallback((id) => {
    swal('Delete Users', 'Are You Sure Delete User?', 'warning', {
      buttons: { cancel: 'Cancel', delete: { text: 'Delete', value: 'delete' } },
      dangerMode: true
    }).then(async (value) => {
      if (value !== 'delete') return

      try {
        await userService.deleteUser(id)
        await swal('Success!', 'User deleted', 'success')
        setReloadKey((current) => current + 1)
      } catch (error) {
        const caught = CatchError(error, navigate, refreshToken)
        if (caught !== undefined) swal('Error', 'Failed to delete user', 'error')
      }
    })
  }, [navigate, refreshToken, userService])

  const columns = useMemo(() => [
    { header: 'ID', accessorKey: 'id', maxWidth: 50 },
    { header: 'Username', accessorKey: 'name'},
    { header: 'Email', accessorKey: 'email'},
    { header: 'Tipe', accessorKey: 'type'},
    {
      header: 'Action',
      id: 'actions',
      enableSorting: false,
      filterable: false,
      exportable: false,
      maxWidth: 150,
      cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button type="button" onClick={() => handleView(row.original.id)} className="btn btn-primary" title="Edit User">
            <i className="fa-solid fa-pen-to-square" />
          </button>
          <button type="button" onClick={() => handleDelete(row.original.id)} className="btn btn-danger" title="Delete User">
            <i className="fa-solid fa-trash-can" />
          </button>
        </div>
      )
    }
  ], [handleDelete, handleView])

  const menu = (
    <div className="text-center">
      <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-success" title="Tambah User">
        <i className="fa-solid fa-pen-to-square" />&nbsp;&nbsp; Tambah User
      </button>
    </div>
  )

  return (
    <div className="page-content">
      <section>
        <CommonListApiPaginated
          header="List User"
          subtitle=""
          columns={columns}
          getData={getUsers}
          menu={menu}
          reloadKey={reloadKey}
          loadingLabel="Loading users..."
          emptyLabel="No users found"
          onError={handleListError}
        />
      </section>

      <AddUserModal
        show={showAddModal}
        submitting={submitting}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddSubmit}
      />
    </div>
  )
}

export default UserManager
